import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { IoService } from '../io/io.service';
import { ClientService } from '../client/client.service';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { DeviceSettingsService } from '../device-settings/device-settings.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { TransferOp, TransferOpKind, TransferQueueDb, transferQueueDbName, purchaseLineRows, transferLabelOf } from './transfer-queue-db';

export { TransferOp, TransferOpKind, transferLabelOf } from './transfer-queue-db';

// Failed changes of one transfer, as the sync sheet lists them: named as well
// as this device can, with the reason the transfer refused them (when known).
export interface FailedGroup {
  purchaseId: string;
  label: string;
  type: string | null;
  status: string | null;
  detail: any;
  count: number;
  sample: string[];
}

export interface QueueStatus {
  pending: number;
  failed: number;
  replaying: boolean;
  lastPushedAt: Date | null;
  oldestPendingAt: Date | null;
}

// Lines the server returned after applying a batch: the component merges
// them (contributions, derived qty, the on-hand it filled in).
export interface AppliedBatch {
  purchaseId: string;
  lines: Array<{ index: number; item: any }>;
  results: Array<{ opId: string; status: string; msg?: string; key?: string }>;
  status: string;
  itemCount: number;
  // Set when the server refused the whole batch because of the transfer's
  // state (deleted, complete …) — the page stops scanning into it.
  refused?: { status: string | null; transfer: any };
}

const BATCH_SIZE = 200;
// A batch request that has not answered by then counts as a transient failure
// (the queue keeps the ops and retries). Without a bound, a request that never
// completes — Wi-Fi dropped mid-request, a stalled connection — left `replaying`
// set for good: every later replay returned at once, the dot said "Sending …"
// forever and nothing short of a reload sent another scan.
const REPLAY_TIMEOUT_MS = 60 * 1000;
const STALE_AFTER_MS = 15 * 60 * 1000;
const STALE_CHECK_MS = 30 * 1000;

/**
 * Persisted queue of transfer line changes made on this device. Every change
 * on a cache-enabled device goes through here — online it drains within a
 * moment, offline it waits — so a wifi drop never loses a scan. Also keeps the
 * last loaded copy of each open transfer so it can be reopened offline.
 */
@Injectable({
  providedIn: 'root'
})
export class TransferQueueService {
  // Offline-copy snapshots: lines are written in chunks of this size with a
  // yield between them; a newer snapshot of a transfer supersedes a running one.
  private static readonly SNAPSHOT_CHUNK = 200;        // inline fallback (no Worker)
  private static readonly SNAPSHOT_POST_CHUNK = 1000;  // lines per message to the worker
  private snapshotTokens: { [purchaseId: string]: number } = {};
  private patchedDuringSnapshot: { [purchaseId: string]: { [index: number]: any } } = {};

  private db: TransferQueueDb | null = null;
  private status = new BehaviorSubject<QueueStatus>({ pending: 0, failed: 0, replaying: false, lastPushedAt: null, oldestPendingAt: null });
  private applied = new Subject<AppliedBatch>();
  private staleAlert = new Subject<{ oldestPendingAt: Date; pending: number }>();
  private replaying = false;
  private replayStartedAt = 0;
  private replayTimer: any = null;
  private staleTimer: any = null;
  private lastStaleAlertAt: Date | null = null;
  private opCounter = 0;

  constructor(
    private ioService: IoService,
    private clientService: ClientService,
    private connectivityService: ConnectivityService,
    private deviceSettingsService: DeviceSettingsService,
    private authenticationService: AuthenticationService,
  ) {
    this.connectivityService.onlineObservable().subscribe(online => {
      if (online) {
        this.scheduleReplay(0);
      }
    });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.scheduleReplay(0);
      }
    });
    this.authenticationService.authenticationStateChangedObservable().subscribe(() => {
      if (this.authenticationService.isSignedIn()) {
        this.scheduleReplay(0);
      }
    });
    this.refreshStatus().then(() => this.scheduleReplay(0));
    this.staleTimer = setInterval(() => this.checkStale(), STALE_CHECK_MS);
  }

  statusObservable(): Observable<QueueStatus> {
    return this.status.asObservable();
  }

  getStatus(): QueueStatus {
    return this.status.value;
  }

  appliedObservable(): Observable<AppliedBatch> {
    return this.applied.asObservable();
  }

  // Fires when the oldest queued change has waited longer than 15 minutes
  // (and again every 15 minutes while it keeps waiting).
  staleAlertObservable(): Observable<{ oldestPendingAt: Date; pending: number }> {
    return this.staleAlert.asObservable();
  }

  private dbName(): string {
    return transferQueueDbName(this.clientService.getRfId());
  }

  private database(): TransferQueueDb {
    if (!this.db) {
      this.db = new TransferQueueDb(this.dbName());
    }
    return this.db;
  }

  newOpId(): string {
    this.opCounter++;
    return this.deviceSettingsService.getDeviceId() + '-' + Date.now().toString(36) + '-' + this.opCounter.toString(36);
  }

  // ---- queue ---------------------------------------------------------------

  async enqueue(op: Omit<TransferOp, 'id' | 'opId' | 'createdAt' | 'status' | 'attempts'> & { opId?: string }): Promise<TransferOp> {
    // A line's absolute "set" replaces the pending "set" already queued for
    // it (same final state, one op) — unless a replay is in flight, when the
    // queued op may already be on its way to the server.
    if (op.kind === 'set' && !this.replaying) {
      const newest = await this.database().ops.where('purchaseId').equals(op.purchaseId)
        .filter(existing => existing.status === 'pending' && existing.accountId === op.accountId).last();
      if (newest && newest.kind === 'set') {
        const merged: TransferOp = { ...newest, value: op.value, entryMethod: op.entryMethod || newest.entryMethod };
        await this.database().ops.put(merged);
        this.scheduleReplay(150);
        return merged;
      }
    }
    const record: TransferOp = {
      ...op,
      opId: op.opId || this.newOpId(),
      createdAt: new Date().toISOString(),
      status: 'pending',
      attempts: 0,
    };
    record.id = await this.database().ops.add(record);
    await this.refreshStatus();
    this.scheduleReplay(150);
    return record;
  }

  async pendingFor(purchaseId: string): Promise<TransferOp[]> {
    return this.database().ops.where('purchaseId').equals(purchaseId).filter(op => op.status === 'pending').sortBy('id');
  }

  // Puts failed ops back in line (all, or one transfer's) and replays: the
  // recovery for a batch that was marked failed by a transient outage, or
  // after the user regains permission. Returns how many were re-queued.
  async retryFailed(purchaseId: string = null): Promise<number> {
    const db = this.database();
    let failed = await db.ops.where('status').equals('failed').toArray();
    if (purchaseId) {
      failed = failed.filter(op => op.purchaseId === purchaseId);
    }
    if (failed.length > 0) {
      await db.ops.bulkPut(failed.map(op => ({
        ...op, status: 'pending' as const, error: null, errorKey: null, transferStatus: null, transferDetail: null,
      })));
    }
    await this.refreshStatus();
    this.scheduleReplay(0);
    return failed.length;
  }

  async failedFor(purchaseId: string): Promise<TransferOp[]> {
    return this.database().ops.where('purchaseId').equals(purchaseId).filter(op => op.status === 'failed').sortBy('id');
  }

  async dismissFailed(purchaseId: string): Promise<void> {
    const failed = await this.failedFor(purchaseId);
    await this.database().ops.bulkDelete(failed.map(op => op.id));
    await this.refreshStatus();
  }

  // Failed changes of every transfer on this device. The status counts them
  // all, so the sheet must be able to show (and retry or dismiss) the ones
  // that belong to a transfer other than the open one.
  async failedAll(): Promise<TransferOp[]> {
    return this.database().ops.where('status').equals('failed').sortBy('id');
  }

  async dismissFailedExcept(purchaseId: string): Promise<void> {
    const failed = (await this.failedAll()).filter(op => op.purchaseId !== purchaseId);
    await this.database().ops.bulkDelete(failed.map(op => op.id));
    await this.refreshStatus();
  }

  async hasPending(purchaseId: string): Promise<boolean> {
    return (await this.pendingFor(purchaseId)).length > 0;
  }

  // The open transfer turned out to be gone (deleted, completed … elsewhere):
  // its queued changes can never apply, so they become failed now, with the
  // reason, instead of after a push that cannot succeed.
  async failPending(purchaseId: string, error: string, transferStatus: string, transferDetail: any = null): Promise<number> {
    const pending = await this.pendingFor(purchaseId);
    if (pending.length > 0) {
      await this.database().ops.bulkPut(pending.map(op => ({
        ...op, status: 'failed' as const, error, errorKey: 'purchaseFindError', transferStatus, transferDetail: transferDetail || null,
      })));
      await this.refreshStatus();
    }
    return pending.length;
  }

  // Failed changes grouped by transfer. The label comes from the change itself
  // (stored at enqueue), else the server's description of the transfer, else
  // the offline copy's head, else the bare id.
  async failedGroups(): Promise<FailedGroup[]> {
    const groups: { [purchaseId: string]: FailedGroup } = {};
    for (const op of await this.failedAll()) {
      let group = groups[op.purchaseId];
      if (!group) {
        group = { purchaseId: op.purchaseId, label: '', type: null, status: null, detail: null, count: 0, sample: [] };
        groups[op.purchaseId] = group;
      }
      group.count++;
      if (!group.label && op.transferLabel) {
        group.label = op.transferLabel;
      }
      if (!group.status && op.transferStatus) {
        group.status = op.transferStatus;
      }
      if (!group.detail && op.transferDetail) {
        group.detail = op.transferDetail;
      }
      if (group.sample.length < 3) {
        group.sample.push(op.kind + ' · ' + (op.error || 'Could not apply.'));
      }
    }
    const result = Object.values(groups);
    for (const group of result) {
      const head = await this.loadPurchaseHead(group.purchaseId);
      group.type = (group.detail && group.detail.type) || (head && head.type) || null;
      if (!group.label) {
        group.label = (group.detail && transferLabelOf(group.detail)) || (head && transferLabelOf(head)) || group.purchaseId;
      }
    }
    return result;
  }

  // The offline copy's head (no lines), even when its line snapshot never finished.
  async loadPurchaseHead(purchaseId: string): Promise<any | null> {
    try {
      const row = await this.database().purchases.get(String(purchaseId));
      return row && row.purchase ? row.purchase : null;
    } catch (e) {
      return null;
    }
  }

  private async refreshStatus() {
    try {
      const db = this.database();
      const pendingOps = await db.ops.where('status').equals('pending').sortBy('createdAt');
      const failed = await db.ops.where('status').equals('failed').count();
      const oldest = pendingOps.length > 0 ? new Date(pendingOps[0].createdAt) : null;
      this.status.next({ ...this.status.value, pending: pendingOps.length, failed, oldestPendingAt: oldest });
    } catch (e) {
      console.error('transfer queue: status failed', e);
    }
  }

  scheduleReplay(delayMs: number) {
    if (this.replayTimer) {
      clearTimeout(this.replayTimer);
    }
    this.replayTimer = setTimeout(() => {
      this.replayTimer = null;
      this.replay();
    }, delayMs);
  }

  // Drains the queue, one transfer at a time, in the order the changes were
  // made. Stops (and keeps everything) at the first network failure.
  // The user's "Send now": a replay that has been running past its timeout is
  // abandoned (its request, if it ever lands, is acknowledged as a duplicate
  // by the server's opId check) and a fresh one starts at once.
  forceReplay() {
    console.log('transfer queue: manual send');
    if (this.replaying && Date.now() - this.replayStartedAt > REPLAY_TIMEOUT_MS) {
      console.warn('transfer queue: abandoning a replay that never finished');
      this.replaying = false;
    }
    this.scheduleReplay(0);
  }

  private postWithTimeout(path: string, body: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          reject({ status: 408, timeout: true });
        }
      }, REPLAY_TIMEOUT_MS);
      this.ioService.post(path, body).then(
        response => { if (!settled) { settled = true; clearTimeout(timer); resolve(response); } },
        error => { if (!settled) { settled = true; clearTimeout(timer); reject(error); } },
      );
    });
  }

  async replay(): Promise<void> {
    // Watchdog: a replay stuck past its timeout (every await inside is bounded,
    // so this should not happen) must not block the queue for good.
    if (this.replaying && Date.now() - this.replayStartedAt > REPLAY_TIMEOUT_MS + 15000) {
      console.warn('transfer queue: previous replay never finished; resetting');
      this.replaying = false;
    }
    if (this.replaying || !this.connectivityService.isOnline() || !this.authenticationService.isSignedIn()) {
      return;
    }
    this.replaying = true;
    this.replayStartedAt = Date.now();
    this.status.next({ ...this.status.value, replaying: true });
    const db = this.database();
    try {
      let pendingOps = await db.ops.where('status').equals('pending').sortBy('id');
      while (pendingOps.length > 0) {
        const purchaseId = pendingOps[0].purchaseId;
        const batch = pendingOps.filter(op => op.purchaseId === purchaseId).slice(0, BATCH_SIZE);
        let response: AppliedBatch;
        try {
          response = (await this.postWithTimeout('/transfer/applyItemOps', {
            purchaseId,
            deviceId: this.deviceSettingsService.getDeviceId(),
            deviceName: this.deviceName(),
            ops: batch.map(op => ({
              opId: op.opId,
              kind: op.kind,
              accountId: op.accountId,
              value: op.value,
              set: op.set,
              fieldKey: op.fieldKey,
              entryMethod: op.entryMethod,
              createdAt: op.createdAt,
            })),
          })) as AppliedBatch;
        } catch (e) {
          if (!e || e.status === 0 || e === 'Expired authorization.') {
            // Offline or locked out: leave the queue as it is.
            return;
          }
          // A real refusal comes back as JSON with a message (transfer
          // completed/deleted, no permission). Anything else — a gateway
          // 502/503/504 while the API restarts, a timeout, an HTML error page,
          // a 404 from an API that is mid-deploy — is transient: keep the ops
          // pending and try again later instead of marking 200 scans failed.
          const serverMsg = e && e.error && typeof e.error.msg === 'string' ? e.error.msg : null;
          const status = e && typeof e.status === 'number' ? e.status : null;
          if (!serverMsg && (status === null || status >= 500 || status === 404 || status === 408 || status === 429)) {
            console.warn('transfer queue: replay deferred (transient error)', e && e.timeout ? 'request timed out' : status);
            this.scheduleReplay(15000);
            return;
          }
          // The whole batch was refused (transfer completed/deleted, no
          // permission): mark these ops failed so the user sees why.
          const msg = serverMsg || 'The server refused the queued changes.';
          const errorKey = e && e.error && e.error.key ? String(e.error.key) : null;
          // purchaseFindError with no status is an older API: the transfer is
          // gone, reason unknown. Any other refusal (no permission) keeps null.
          const transferStatus = e && e.error && e.error.status
            ? String(e.error.status)
            : (errorKey === 'purchaseFindError' ? 'missing' : null);
          const transferDetail = e && e.error && e.error.transfer ? e.error.transfer : null;
          await db.ops.bulkPut(batch.map(op => ({
            ...op, status: 'failed' as const, attempts: op.attempts + 1, error: msg, errorKey, transferStatus, transferDetail,
          })));
          this.applied.next({
            purchaseId, lines: [], results: batch.map(op => ({ opId: op.opId, status: 'error', msg, key: errorKey })),
            status: e && e.error && e.error.status, itemCount: -1, refused: { status: transferStatus, transfer: transferDetail },
          });
          await this.refreshStatus();
          pendingOps = await db.ops.where('status').equals('pending').sortBy('id');
          continue;
        }
        const results = response.results || [];
        const done: number[] = [];
        const failed: TransferOp[] = [];
        for (const op of batch) {
          const result = results.find(entry => entry.opId === op.opId);
          if (!result || result.status === 'applied' || result.status === 'duplicate') {
            done.push(op.id);
          } else {
            failed.push({ ...op, status: 'failed', attempts: op.attempts + 1, error: result.msg || 'Could not apply.', errorKey: result.key || null });
          }
        }
        await db.transaction('rw', db.ops, async () => {
          if (done.length > 0) { await db.ops.bulkDelete(done); }
          if (failed.length > 0) { await db.ops.bulkPut(failed); }
        });
        this.status.next({ ...this.status.value, lastPushedAt: new Date() });
        this.lastStaleAlertAt = null;
        this.applied.next({ ...response, purchaseId });
        await this.refreshStatus();
        pendingOps = await db.ops.where('status').equals('pending').sortBy('id');
      }
    } catch (e) {
      console.error('transfer queue: replay failed', e);
    } finally {
      this.replaying = false;
      this.status.next({ ...this.status.value, replaying: false });
    }
  }

  private deviceName(): string {
    try {
      const deviceAuth = JSON.parse(localStorage.getItem('deviceAuth') || 'null');
      if (deviceAuth && deviceAuth.name) {
        return deviceAuth.name;
      }
    } catch (e) {
    }
    return 'Device ' + this.deviceSettingsService.getDeviceId().slice(-4).toUpperCase();
  }

  private checkStale() {
    const status = this.status.value;
    if (status.pending === 0 || !status.oldestPendingAt) {
      return;
    }
    const waited = Date.now() - status.oldestPendingAt.getTime();
    if (waited < STALE_AFTER_MS) {
      return;
    }
    if (this.lastStaleAlertAt && Date.now() - this.lastStaleAlertAt.getTime() < STALE_AFTER_MS) {
      return;
    }
    this.lastStaleAlertAt = new Date();
    this.staleAlert.next({ oldestPendingAt: status.oldestPendingAt, pending: status.pending });
  }

  // ---- offline copies of open transfers -----------------------------------

  // Writes the whole transfer: the head record, then the lines in chunks
  // with a yield between them so scanning is never blocked for long (a
  // 20k-line count is several MB; one put of it froze handhelds for seconds).
  // A newer snapshot of the same transfer supersedes one still in progress.
  // Full snapshot of an open transfer. Skipped when the stored copy is already
  // this version (same timeUpdated, status and line count — the per-line
  // patches keep it current). Otherwise the head is written here and the lines
  // go to the web worker in chunks, so a 20k-line count never blocks the page
  // thread for more than the few ms a chunk takes to post.
  async savePurchase(purchase: any): Promise<void> {
    if (!purchase || !purchase._id) {
      return;
    }
    const purchaseId = String(purchase._id);
    const token = (this.snapshotTokens[purchaseId] || 0) + 1;
    this.snapshotTokens[purchaseId] = token;
    try {
      const { typeObject, searchResults, bulkAdd, items, ...rest } = purchase;
      const lines: any[] = Array.isArray(items) ? items : [];
      const db = this.database();
      const existing = await db.purchases.get(purchaseId);
      if (existing && existing.complete === true && existing.lineCount === lines.length && existing.purchase
        && String(existing.purchase.status) === String(rest.status)
        && this.sameTime(existing.purchase.timeUpdated, rest.timeUpdated)) {
        return;
      }
      const head = JSON.parse(JSON.stringify(rest));
      if (typeof Worker !== 'undefined') {
        await this.snapshotInWorker(purchaseId, head, lines, token);
      } else {
        await db.purchases.put({ purchaseId, savedAt: new Date().toISOString(), purchase: head, lineCount: lines.length, complete: false });
        await db.purchaseLines.where('purchaseId').equals(purchaseId).delete();
        for (let start = 0; start < lines.length; start += TransferQueueService.SNAPSHOT_CHUNK) {
          if (this.snapshotTokens[purchaseId] !== token) {
            return;
          }
          await this.savePurchaseLines(purchaseId, lines.slice(start, start + TransferQueueService.SNAPSHOT_CHUNK));
          await new Promise(resolve => setTimeout(resolve, 40));
        }
        await db.purchases.update(purchaseId, { complete: true });
      }
      // Lines patched while the snapshot was being written win over the
      // snapshot's older copy of them.
      const patched = this.patchedDuringSnapshot[purchaseId];
      delete this.patchedDuringSnapshot[purchaseId];
      if (patched && this.snapshotTokens[purchaseId] === token) {
        await this.savePurchaseLines(purchaseId, Object.values(patched));
      }
    } catch (e) {
      console.error('transfer queue: purchase cache failed', e);
    }
  }

  private sameTime(a: any, b: any): boolean {
    const ta = a ? new Date(a).getTime() : 0;
    const tb = b ? new Date(b).getTime() : 0;
    return ta === tb;
  }

  private snapshotInWorker(purchaseId: string, head: any, lines: any[], token: number): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      // tslint:disable-next-line:whitespace (tslint predates import.meta)
      const worker = new Worker(new URL('../inventory-cache/inventory-sync.worker', import.meta.url), { type: 'module' });
      this.patchedDuringSnapshot[purchaseId] = {};
      const finish = (error?: any) => {
        worker.terminate();
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      };
      worker.onmessage = ({ data }) => {
        if (!data) { return; }
        if (data.type === 'snapshotDone') {
          finish();
        } else if (data.type === 'snapshotError') {
          finish(new Error(data.message || 'Snapshot failed.'));
        }
      };
      worker.onerror = (event) => finish(new Error((event && event.message) || 'The snapshot worker failed.'));
      worker.onmessageerror = () => finish(new Error('The snapshot worker could not read a message.'));
      const dbName = this.dbName();
      worker.postMessage({ cmd: 'snapshotStart', dbName, purchaseId, head, lineCount: lines.length });
      const post = (start: number) => {
        if (this.snapshotTokens[purchaseId] !== token) {
          finish();
          return;
        }
        if (start >= lines.length) {
          worker.postMessage({ cmd: 'snapshotEnd', purchaseId });
          return;
        }
        try {
          const chunk = lines.slice(start, start + TransferQueueService.SNAPSHOT_POST_CHUNK);
          worker.postMessage({ cmd: 'snapshotLines', purchaseId, lines: chunk });
        } catch (e) {
          // A value that cannot be cloned: fall back to the JSON form.
          const chunk = lines.slice(start, start + TransferQueueService.SNAPSHOT_POST_CHUNK);
          worker.postMessage({ cmd: 'snapshotLines', purchaseId, lines: JSON.parse(JSON.stringify(chunk)) });
        }
        setTimeout(() => post(start + TransferQueueService.SNAPSHOT_POST_CHUNK), 0);
      };
      post(0);
    });
  }

  // Updates just these lines in the offline copy (after a replayed batch).
  async savePurchaseLines(purchaseId: string, lines: any[]): Promise<void> {
    if (!purchaseId || !lines || lines.length === 0) {
      return;
    }
    try {
      const rows = purchaseLineRows(purchaseId, lines.map(line => JSON.parse(JSON.stringify(line))));
      const inFlight = this.patchedDuringSnapshot[purchaseId];
      if (inFlight) {
        for (const row of rows) {
          inFlight[row.index] = row.line;
        }
      }
      if (rows.length > 0) {
        await this.database().purchaseLines.bulkPut(rows);
      }
    } catch (e) {
      console.error('transfer queue: purchase line cache failed', e);
    }
  }

  async loadPurchase(purchaseId: string): Promise<any | null> {
    try {
      const db = this.database();
      const row = await db.purchases.get(String(purchaseId));
      if (!row) {
        return null;
      }
      if (row.complete === false) {
        return null;
      }
      const purchase = row.purchase || {};
      const rows = await db.purchaseLines.where('purchaseId').equals(String(purchaseId)).sortBy('index');
      if (rows.length > 0) {
        purchase.items = rows.map(entry => entry.line);
      } else if (!Array.isArray(purchase.items)) {
        purchase.items = [];
      }
      return purchase;
    } catch (e) {
      return null;
    }
  }

  async saveMeta(key: string, value: any): Promise<void> {
    try {
      await this.database().meta.put({ key, value, savedAt: new Date().toISOString() });
    } catch (e) {
    }
  }

  async loadMeta(key: string): Promise<any> {
    try {
      const row = await this.database().meta.get(key);
      return row ? row.value : null;
    } catch (e) {
      return null;
    }
  }
}
